import ecsLogger from 'utilities/logging/loggerConfig';
import apmInstance from 'utilities/loggerv2/apmInit';
import { LogContext, Logger } from './logger';
import LogLevel from './loglevel';
import ErrorCodeDefinition from './errorCodeDefinition';

class ServerLogger implements Logger {
  private serviceName: string;

  private userId?: string;

  private sessionId?: string;

  private apmAgent: any;

  constructor(serviceName: string) {
    this.serviceName = serviceName;
    this.apmAgent = apmInstance;
  }

  setUserId(userId: string): void {
    this.userId = userId;
    this.apmAgent.setUserContext({
      id: userId,
    });
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  private enrichContext(context?: LogContext): LogContext {
    return {
      ...context,
      service: this.serviceName,
      ...(this.userId && { userId: this.userId }),
      ...(this.sessionId && { sessionId: this.sessionId }),
    };
  }

  debug(message: string, context?: LogContext): void {
    ecsLogger.debug(message, this.enrichContext(context));
  }

  info(message: string, context?: LogContext): void {
    ecsLogger.info(message, this.enrichContext(context));
  }

  warn(message: string, context?: LogContext): void {
    ecsLogger.warn(message, this.enrichContext(context));
  }

  error(message: string, error?: Error, context?: LogContext): void {
    const enrichedContext = this.enrichContext(context);

    ecsLogger.error(message, {
      ...enrichedContext,
      ...(error && {
        error: {
          message: error.message,
          stack_trace: error.stack,
        },
      }),
    });

    if (error) {
      this.apmAgent.captureError(error, {
        message,
        custom: enrichedContext,
      });
    }
  }

  fatal(message: string, error?: Error, context?: LogContext): void {
    const enrichedContext = this.enrichContext(context);

    ecsLogger.error(`FATAL: ${message}`, {
      ...enrichedContext,
      ...(error && {
        error: {
          message: error.message,
          stack_trace: error.stack,
        },
      }),
      isFatal: true,
    });

    if (error) {
      this.apmAgent.captureError(error, {
        message: `FATAL: ${message}`,
        custom: { ...enrichedContext, isFatal: true },
      });
    } else {
      this.apmAgent.captureError(new Error(message), {
        custom: { ...enrichedContext, isFatal: true },
      });
    }
  }

  logWithCode(errorCode: ErrorCodeDefinition, error?: Error, context?: LogContext): void {
    // Add validation to ensure errorCode is properly defined
    if (!errorCode || typeof errorCode !== 'object') {
      console.error('Invalid error code provided to logWithCode', errorCode);
      this.error('Invalid error code provided to logger', error, context);
      return;
    }

    const fullMessage = `[${errorCode.code || 'UNKNOWN_CODE'}] ${errorCode.message || 'Unknown error'}`;
    const enrichedContext = {
      ...this.enrichContext(context),
      errorCode: errorCode.code || 'UNKNOWN_CODE',
      errorCategory: errorCode.category || 'UNKNOWN',
      errorDescription: errorCode.description || '',
    };
    switch (errorCode.level) {
      case LogLevel.DEBUG:
        this.debug(fullMessage, enrichedContext);
        break;
      case LogLevel.INFO:
        this.info(fullMessage, enrichedContext);
        break;
      case LogLevel.WARN:
        this.warn(fullMessage, enrichedContext);
        break;
      case LogLevel.ERROR:
        this.error(fullMessage, error, enrichedContext);
        break;
      case LogLevel.FATAL:
        this.fatal(fullMessage, error, enrichedContext);
        break;
      default:
        this.info(fullMessage, enrichedContext);
    }

    // Always send error codes to APM for metrics
    if (error || errorCode.level === LogLevel.ERROR || errorCode.level === LogLevel.FATAL) {
      this.apmAgent.captureError(error || new Error(fullMessage), {
        message: fullMessage,
        labels: { code: errorCode.code },
        tags: { category: errorCode.category },
        custom: enrichedContext,
      });
    }
  }
}

export default ServerLogger;
